import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { investigationService } from '../../services/api';
import TopBar from '../../components/TopBar';

const emptyForm = { name: '' };

const InvestigationManagement = () => {
  const navigate = useNavigate();
  const [investigations, setInvestigations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredInvestigations, setFilteredInvestigations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await investigationService.getAllInvestigations();
      setInvestigations(res.data);
      setFilteredInvestigations(res.data);
      setSearchQuery('');
    } catch (err) {
      console.error('Failed to load investigations:', err);
    }
  };

  const handleSearch = () => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      setFilteredInvestigations(investigations);
    } else {
      setFilteredInvestigations(
        investigations.filter((t) => t.name.toLowerCase().includes(q))
      );
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ name: item.name });
    } else {
      setEditingItem(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await investigationService.updateInvestigation(editingItem.id, { id: editingItem.id, name: formData.name });
      } else {
        await investigationService.createInvestigation({ name: formData.name });
      }
      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Failed to save investigation:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this investigation?')) {
      try {
        await investigationService.deleteInvestigation(id);
        loadData();
      } catch (err) {
        console.error('Failed to delete investigation:', err);
      }
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Investigation', flex: 1, minWidth: 200 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenDialog(params.row)}
            title="Edit"
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id)}
            title="Delete"
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Investigation Management" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            {/* Search */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search investigation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                sx={{ width: 260 }}
              />
              <Button
                variant="outlined"
                startIcon={<Search />}
                onClick={handleSearch}
              >
                Search
              </Button>
            </Box>

            {/* Add */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add Investigation
            </Button>
          </Box>

          <DataGrid
            rows={filteredInvestigations}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            autoHeight
            disableSelectionOnClick
          />
        </Paper>
      </Container>

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Investigation' : 'Add Investigation'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              {editingItem && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="ID"
                    value={editingItem.id}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={editingItem ? 8 : 12}>
                <TextField
                  fullWidth
                  label="Investigation Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ name: e.target.value })}
                  required
                  autoFocus
                  placeholder="e.g., Complete Blood Count (CBC)"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingItem ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default InvestigationManagement;
